import { api } from './api';

export type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';
export type ProblemStatus = 'NOT_STARTED' | 'ATTEMPTED' | 'SOLVED';
export type SubmissionStatus =
  | 'ACCEPTED'
  | 'WRONG_ANSWER'
  | 'TIME_LIMIT_EXCEEDED'
  | 'RUNTIME_ERROR'
  | 'PENDING';

export interface AlgoProblemSummary {
  id: string;
  slug: string;
  title: string;
  difficulty: Difficulty;
  topics: string[];
  order: number;
  status: ProblemStatus;
  attempts: number;
  solvedAt: string | null;
}

export interface AlgoTestCase {
  id: string;
  input: string;
  expected: string;
  isHidden: boolean;
  order: number;
}

export interface AlgoProblemDetail extends AlgoProblemSummary {
  description: string;
  examples: string;
  constraints: string;
  starterCode: string;
  youtubeId: string | null;
  testCases: AlgoTestCase[];
}

export interface TestCaseResult {
  passed: boolean;
  input: string;
  expected: string;
  actual: string;
  runtime: number;
  error?: string;
}

export interface RunResult {
  results: TestCaseResult[];
  allPassed: boolean;
  totalRuntime: number;
  executionMode: 'heuristic';
  limitations: string[];
}

export interface AlgoSubmission {
  id: string;
  code: string;
  language: string;
  status: SubmissionStatus;
  runtime: number | null;
  errorMessage: string | null;
  failedInput: string | null;
  failedExpected: string | null;
  failedActual: string | null;
  createdAt: string;
}

export interface SubmitResult {
  submission: AlgoSubmission;
  runResult: RunResult;
}

export interface ProblemsPage {
  problems: AlgoProblemSummary[];
  total: number;
  page: number;
  pages: number;
}

export interface AlgoStats {
  total: number;
  solved: number;
  attempted: number;
  byDifficulty: { EASY: number; MEDIUM: number; HARD: number };
  totalAttempts: number;
  successRate: number;
}

export const algorithmsService = {
  listProblems: (params?: {
    difficulty?: Difficulty;
    topic?: string;
    page?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.difficulty) qs.set('difficulty', params.difficulty);
    if (params?.topic) qs.set('topic', params.topic);
    if (params?.page) qs.set('page', String(params.page));
    const query = qs.toString();
    return api.get<ProblemsPage>(`/algorithms/problems${query ? `?${query}` : ''}`);
  },

  getProblem: (slug: string) =>
    api.get<AlgoProblemDetail>(`/algorithms/problems/${slug}`),

  getSubmissions: (problemId: string) =>
    api.get<AlgoSubmission[]>(`/algorithms/problems/${problemId}/submissions`),

  run: (problemId: string, code: string, language = 'python') =>
    api.post<RunResult>('/algorithms/run', { problemId, code, language }),

  submit: (problemId: string, code: string, language = 'python') =>
    api.post<SubmitResult>('/algorithms/submit', { problemId, code, language }),

  getStats: () => api.get<AlgoStats>('/algorithms/stats'),
};
